import React from 'react';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { UserDto as User } from '@poker-stats/contracts';

import FormItem from '../../components/FormItem/FormItem';
import Input from '../../components/Input/Input';
import TextArea from '../../components/TextArea/TextArea';
import MailIcon from '../../assets/Icons/MailIcon.svg?react';
import LockIcon from '../../assets/Icons/LockIcon.svg?react';
import SupportIcon from '../../assets/Icons/SupportIcon.svg?react';
import { getPokersiteNameByPokersiteId } from '../../containers/StatPage/utils';
import {
  changeEmail,
  changePassword,
  sendFeedback,
} from '../../containers/UserProfilePage/actions';

const MANAGER_TELEGRAM_URL = 'https://t.me/fishh_support';

function getSubscriptionLabel(subscription: number) {
  switch (subscription) {
    case 1:
      return '10-25';
    case 2:
      return '10-50';
    case 3:
      return '10-200';
    case 4:
      return 'Unlim';
    default:
      return '10-25';
  }
}

type EmailFormValues = {
  email: string;
};

type PasswordFormValues = {
  oldPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};

type SupportFormValues = {
  subject: string;
  message: string;
};

export const V2ProfileEmailPanel: React.FC<{ user: User }> = ({ user }) => {
  const { t } = useTranslation('translation');
  const dispatch = useDispatch();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EmailFormValues>({});

  const onSubmit = handleSubmit(({ email }) => {
    dispatch(changeEmail({ newEmail: email }));
  });

  return (
    <section className="settings-panel profile-panel">
      <div className="settings-panel__head">
        <span className="settings-panel__icon">
          <MailIcon />
        </span>
        <h2>Email</h2>
      </div>

      <form className="profile-panel__form" onSubmit={onSubmit}>
        <div className="profile-meta-box">
          <p className="settings-field-label">{t('current_email')}</p>
          <p className="profile-meta-box__value">{user.email}</p>
          {user.availablePokersiteIds &&
          user.availablePokersiteIds.length > 0 &&
          user.subscription &&
          user.subscriptionDate ? (
            <p className="profile-meta-box__subscription">
              {t('subscription')}: <strong>{getSubscriptionLabel(user.subscription)}</strong>{' '}
              {dayjs(user.subscriptionDate).locale('ru').format('DD MMMM YYYY')}{' '}
              <strong>
                {user.availablePokersiteIds
                  .map((id) => getPokersiteNameByPokersiteId(id))
                  .join(', ')}
              </strong>
            </p>
          ) : null}
        </div>

        <div className="settings-field">
          <p className="settings-field-label">{t('new_email')}</p>
          <FormItem error={errors.email?.message}>
            <Controller
              name="email"
              control={control}
              rules={{
                required: t('required'),
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: t('entered_value_does_not_match_email_format'),
                },
              }}
              render={({ field }) => (
                <Input
                  placeholder={t('new_email')}
                  autoComplete="off"
                  aria-invalid={errors.email?.message ? true : false}
                  {...field}
                />
              )}
            />
          </FormItem>
        </div>

        <div className="settings-panel__footer">
          <button type="submit" className="acid-button">
            {t('change')} <span aria-hidden="true">↗</span>
          </button>
        </div>
      </form>
    </section>
  );
};

export const V2ProfilePasswordPanel: React.FC = () => {
  const { t } = useTranslation('translation');
  const dispatch = useDispatch();
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<PasswordFormValues>({});

  const onSubmit = handleSubmit(({ oldPassword, newPassword, confirmNewPassword }) => {
    dispatch(changePassword({ oldPassword, newPassword, confirmNewPassword }));
  });

  return (
    <section className="settings-panel profile-panel">
      <div className="settings-panel__head">
        <span className="settings-panel__icon">
          <LockIcon />
        </span>
        <h2>{t('password')}</h2>
      </div>

      <form className="profile-panel__form" onSubmit={onSubmit}>
        <div className="settings-fields-row">
          <div className="settings-field">
            <p className="settings-field-label">{t('old_password')}</p>
            <FormItem error={errors.oldPassword?.message}>
              <Controller
                name="oldPassword"
                control={control}
                rules={{ required: t('please_input_your_password') }}
                render={({ field }) => (
                  <Input
                    placeholder={t('old_password')}
                    autoComplete="off"
                    type="password"
                    aria-invalid={errors.oldPassword?.message ? true : false}
                    {...field}
                  />
                )}
              />
            </FormItem>
          </div>

          <div className="settings-field">
            <p className="settings-field-label">{t('new_password')}</p>
            <FormItem error={errors.newPassword?.message}>
              <Controller
                name="newPassword"
                control={control}
                rules={{ required: t('required') }}
                render={({ field }) => (
                  <Input
                    placeholder={t('new_password')}
                    autoComplete="off"
                    type="password"
                    aria-invalid={errors.newPassword?.message ? true : false}
                    {...field}
                  />
                )}
              />
            </FormItem>
          </div>

          <div className="settings-field">
            <p className="settings-field-label">{t('confirm_password')}</p>
            <FormItem error={errors.confirmNewPassword?.message}>
              <Controller
                name="confirmNewPassword"
                control={control}
                rules={{
                  required: t('required'),
                  validate: (value) => {
                    if (watch('newPassword') !== value) {
                      return t('passwords_do_not_match');
                    }
                  },
                }}
                render={({ field }) => (
                  <Input
                    placeholder={t('confirm_password')}
                    autoComplete="off"
                    type="password"
                    aria-invalid={errors.confirmNewPassword?.message ? true : false}
                    {...field}
                  />
                )}
              />
            </FormItem>
          </div>
        </div>

        <div className="settings-panel__footer">
          <button type="submit" className="acid-button">
            {t('change')} <span aria-hidden="true">↗</span>
          </button>
        </div>
      </form>
    </section>
  );
};

export const V2ProfileSupportPanel: React.FC = () => {
  const { t } = useTranslation('translation');
  const dispatch = useDispatch();
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<SupportFormValues>({});

  const onSubmit = handleSubmit(({ subject, message }) => {
    dispatch(sendFeedback({ subject, message }));
    reset();
  });

  return (
    <section className="settings-panel profile-panel">
      <div className="settings-panel__head">
        <span className="settings-panel__icon">
          <SupportIcon />
        </span>
        <h2>{t('support')}</h2>
      </div>

      <div className="profile-support-grid">
        <aside className="profile-support-aside">
          <p className="settings-field-hint">
            Быстрее всего ответим в Telegram — или отправьте сообщение через форму справа.
          </p>
          <a
            className="profile-support-contact"
            href={MANAGER_TELEGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="profile-support-contact__icon" aria-hidden="true">
              TG
            </span>
            <span className="profile-support-contact__copy">
              <strong>Telegram</strong>
              <span>@fishh_support</span>
            </span>
            <span className="profile-support-contact__arrow" aria-hidden="true">
              ↗
            </span>
          </a>
          <a
            className="acid-button profile-support-contact__btn"
            href={MANAGER_TELEGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Написать в Telegram <span aria-hidden="true">↗</span>
          </a>
        </aside>

        <form className="profile-support-form" onSubmit={onSubmit}>
          <p className="settings-field-hint">{t('ask_a_question_through_the_feedback_form')}</p>

          <div className="settings-field">
            <p className="settings-field-label">{t('subject_of_the_message')}</p>
            <FormItem>
              <Controller
                name="subject"
                control={control}
                render={({ field }) => (
                  <Input placeholder={t('subject_of_the_message')} autoComplete="off" {...field} />
                )}
              />
            </FormItem>
          </div>

          <div className="settings-field">
            <p className="settings-field-label">{t('describe_the_problem')}</p>
            <FormItem error={errors.message?.message}>
              <Controller
                name="message"
                control={control}
                rules={{ required: t('please_describe_your_problem') }}
                render={({ field }) => (
                  <TextArea
                    placeholder={t('describe_the_problem')}
                    autoSize={{ minRows: 5, maxRows: 8 }}
                    aria-invalid={errors.message?.message ? true : false}
                    {...field}
                  />
                )}
              />
            </FormItem>
          </div>

          <div className="settings-panel__footer profile-support-form__footer">
            <button type="submit" className="acid-button">
              {t('send')} <span aria-hidden="true">↗</span>
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};
